/**
 * DEC vs HEX handling for RFID tag values — person tags (`tag_value`) and vehicle
 * tags (`rfid_tag`).
 *
 * Readers in the field report the same physical card in either base, and the
 * stored string is all this system ever sees afterwards. Three of the four forms
 * say which base they are:
 *
 *   "042,12528"   Wiegand "facility,card"        — base does not apply
 *   "0x2A30F0"    explicit 0x prefix             — hex
 *   "2A30F0"      contains A-F                   — hex
 *   "0253985"     leading zero = padded hex      — hex (readers pad hex to a fixed width)
 *
 * The fourth does not: "989390" is all digits with no leading zero, so it is
 * either decimal 989390 or hex 0x989390 = 9999248 — two different physical cards.
 * Every consumer resolves that tie by assuming decimal (see
 * deviceCommunication.parseRfidTagToCardValue for the canonical parse table),
 * which is a coin flip that silently writes the wrong card number to the reader
 * for every tag that was actually hex.
 *
 * So the tie is broken where the information actually exists — the operator
 * typing the value in — and the answer is persisted inside the value itself by
 * padding a leading zero, which is what the readers themselves do and what every
 * consumer already reads back as hex. Nothing new to store, and a tag saved
 * through the prompt is indistinguishable from one a reader emitted padded.
 */

export type TagRadix = 'dec' | 'hex';

export type TagValueRadixForm =
  /** blank input — nothing to ask about yet */
  | 'empty'
  /** "042,12528" — facility,card; downstream encodings disagree on the math, so this util stays out of it */
  | 'wiegand'
  /** "0x2A30F0" or "2A30F0" — self-describing hex */
  | 'hex-explicit'
  /** "0253985" — leading zero, the reader's own hex padding */
  | 'hex-padded'
  /** single digit: 0-9 mean the same number in both bases, so there is no tie to break */
  | 'radix-agnostic'
  /** "989390" — all digits, no leading zero: decimal or hex, and the string cannot tell us */
  | 'ambiguous'
  /** not a tag value at all (letters past F, spaces, punctuation) */
  | 'non-numeric';

export function classifyTagValueRadix(raw: string | null | undefined): TagValueRadixForm {
  const t = (raw || '').trim();
  if (!t) return 'empty';
  if (t.includes(',')) return 'wiegand';
  if (/^0[xX][0-9a-fA-F]+$/.test(t)) return 'hex-explicit';
  if (/^[0-9a-fA-F]+$/.test(t) && /[a-fA-F]/.test(t)) return 'hex-explicit';
  if (/^0\d+$/.test(t)) return 'hex-padded';
  if (/^\d$/.test(t)) return 'radix-agnostic';
  if (/^\d+$/.test(t)) return 'ambiguous';
  return 'non-numeric';
}

/** True when the string alone cannot say whether it is decimal or hex — i.e. when the operator must be asked. */
export function isAmbiguousTagValue(raw: string | null | undefined): boolean {
  return classifyTagValueRadix(raw) === 'ambiguous';
}

/**
 * Records "this all-digit value is hex" the way every consumer already reads it back:
 * by padding a leading zero. A no-op for anything that already states its own base,
 * so it can never double-pad a value on re-save.
 */
export function markTagValueAsHex(raw: string | null | undefined): string {
  const t = (raw || '').trim();
  return isAmbiguousTagValue(t) ? `0${t}` : t;
}

/**
 * Applies the operator's answer to a value on its way to the API. Anything that is
 * already self-describing passes through untouched regardless of `radix`.
 */
export function applyTagRadix(raw: string | null | undefined, radix: TagRadix | null): string {
  const t = (raw || '').trim();
  return radix === 'hex' ? markTagValueAsHex(t) : t;
}

/**
 * The decimal card number a tag string resolves to, using `assumed` only for the
 * ambiguous form. Returns null for Wiegand — two conflicting "facility,card"
 * encodings live downstream (Guarita fc<<16|cn, ControlID fc<<32|cn) and this util
 * will not add a third opinion — and for anything unparseable.
 */
export function tagValueToDecimal(raw: string | null | undefined, assumed: TagRadix = 'dec'): number | null {
  const t = (raw || '').trim();
  let n: number;
  switch (classifyTagValueRadix(t)) {
    case 'hex-explicit':
      n = parseInt(/^0[xX]/.test(t) ? t.slice(2) : t, 16);
      break;
    case 'hex-padded':
      n = parseInt(t, 16);
      break;
    case 'radix-agnostic':
      n = parseInt(t, 10);
      break;
    case 'ambiguous':
      n = parseInt(t, assumed === 'hex' ? 16 : 10);
      break;
    default:
      return null;
  }
  // Beyond Number.MAX_SAFE_INTEGER, parseInt silently rounds — a 24-hex-digit
  // UHF EPC would resolve to an imprecise float and register a WRONG card
  // number on the device. Reject instead (Go's int64 parser errors on the same
  // inputs — see condo-manager handlers/testdata/rfid_tag_vectors.json).
  return Number.isNaN(n) || !Number.isSafeInteger(n) ? null : n;
}
